import { Extension, Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import Mention from '@tiptap/extension-mention'

export const formatFileSize = (bytes) => {
  const size = Number(bytes)
  if (!size) {
    return ''
  }
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1)
  const value = size / Math.pow(1024, exponent)
  return `${value.toFixed(exponent === 0 || value >= 10 ? 0 : 1)} ${units[exponent]}`
}

const dataAttribute = (name, attribute) => ({
  default: null,
  parseHTML: element => element.getAttribute(attribute),
  renderHTML: attributes => (attributes[name] ? { [attribute]: attributes[name] } : {})
})

const mediaNode = (name, tag) => Node.create({
  name,
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null }
    }
  },

  parseHTML() {
    return [{ tag: `${tag}[src]` }]
  },

  renderHTML({ HTMLAttributes }) {
    return [tag, mergeAttributes({ controls: 'true', preload: 'metadata', class: `rich-${name}` }, HTMLAttributes)]
  }
})

export const Video = mediaNode('video', 'video')
export const Audio = mediaNode('audio', 'audio')

export const FileAttachment = Node.create({
  name: 'fileAttachment',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      href: dataAttribute('href', 'href'),
      name: dataAttribute('name', 'data-name'),
      size: dataAttribute('size', 'data-size')
    }
  },

  parseHTML() {
    // Higher than the link mark so the whole card is parsed as one node
    return [{ tag: 'a[data-type="file-attachment"]', priority: 100 }]
  },

  renderHTML({ node, HTMLAttributes }) {
    const { name, size, href } = node.attrs
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'file-attachment',
        class: 'rich-file',
        target: '_blank',
        rel: 'noopener noreferrer'
      }),
      ['span', { class: 'rich-file-icon' }, '📎'],
      ['span', { class: 'rich-file-name' }, name || href || 'File'],
      ['span', { class: 'rich-file-size' }, formatFileSize(size)]
    ]
  }
})

export const Bookmark = Node.create({
  name: 'bookmark',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      url: dataAttribute('url', 'href'),
      title: dataAttribute('title', 'data-title'),
      description: dataAttribute('description', 'data-description'),
      image: dataAttribute('image', 'data-image'),
      favicon: dataAttribute('favicon', 'data-favicon')
    }
  },

  parseHTML() {
    return [{ tag: 'a[data-type="bookmark"]', priority: 100 }]
  },

  renderHTML({ node, HTMLAttributes }) {
    const { url, title, description, image, favicon } = node.attrs
    const urlLine = ['span', { class: 'rich-bookmark-url' }]
    if (favicon) {
      urlLine.push(['img', { src: favicon, alt: '' }])
    }
    urlLine.push(url || '')
    const card = [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'bookmark',
        class: 'rich-bookmark',
        target: '_blank',
        rel: 'noopener noreferrer'
      }),
      [
        'span',
        { class: 'rich-bookmark-body' },
        ['span', { class: 'rich-bookmark-title' }, title || url || ''],
        ['span', { class: 'rich-bookmark-description' }, description || ''],
        urlLine
      ]
    ]
    if (image) {
      card.push(['span', { class: 'rich-bookmark-image' }, ['img', { src: image, alt: '' }]])
    }
    return card
  }
})

const pagePaths = {
  advice: id => `/advices/${id}`,
  legalChange: id => `/legal-changes/${id}`
}

// @mentions of people and pages. People render as a span, pages as a link
// to the advice or legal change.
export const PageAndPeopleMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent(),
      kind: {
        default: 'user',
        parseHTML: element => element.getAttribute('data-mention-type') || 'user',
        renderHTML: attributes => ({ 'data-mention-type': attributes.kind })
      }
    }
  },

  parseHTML() {
    return [
      { tag: 'span[data-type="mention"]' },
      { tag: 'a[data-type="mention"]', priority: 100 }
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const { kind, id, label } = node.attrs
    const attributes = mergeAttributes({ 'data-type': 'mention', class: `mention mention-${kind}` }, HTMLAttributes)
    if (pagePaths[kind]) {
      return ['a', { ...attributes, href: pagePaths[kind](id) }, label || '']
    }
    return ['span', attributes, `@${label || ''}`]
  },

  renderText({ node }) {
    return node.attrs.kind === 'user' ? `@${node.attrs.label}` : node.attrs.label
  }
})

// Uploads files dropped or pasted into the editor. Clipboard content with
// text or HTML (e.g. from Word) is left to the normal paste handling.
export const FileDrop = Extension.create({
  name: 'fileDrop',

  addOptions() {
    return {
      onFiles: () => {}
    }
  },

  addProseMirrorPlugins() {
    const { onFiles } = this.options
    return [
      new Plugin({
        key: new PluginKey('fileDrop'),
        props: {
          handleDrop: (view, event, slice, moved) => {
            const files = Array.from((event.dataTransfer && event.dataTransfer.files) || [])
            if (moved || !files.length) {
              return false
            }
            event.preventDefault()
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY })
            onFiles(files, coordinates ? coordinates.pos : view.state.selection.from)
            return true
          },
          handlePaste: (view, event) => {
            const clipboard = event.clipboardData
            const files = Array.from((clipboard && clipboard.files) || [])
            if (!files.length || clipboard.getData('text/html') || clipboard.getData('text/plain')) {
              return false
            }
            onFiles(files, view.state.selection.from)
            return true
          }
        }
      })
    ]
  }
})

// Node content for an uploaded or linked file, chosen by its type
export const nodeForFile = ({ url, name, size, type }) => {
  if (type.startsWith('image/')) {
    return { type: 'image', attrs: { src: url, alt: name } }
  }
  if (type.startsWith('video/')) {
    return { type: 'video', attrs: { src: url } }
  }
  if (type.startsWith('audio/')) {
    return { type: 'audio', attrs: { src: url } }
  }
  return { type: 'fileAttachment', attrs: { href: url, name, size } }
}
